import { TprmRequirementType } from '@evidentid/tprm-portal-lib/models/entity-details';

import { FieldMeta } from '@/modules/dynamic-form/models/FieldMeta.model';
import { FieldSchema } from '@/modules/dynamic-form/models/FieldSchema.model';

import { toFieldPath } from '../../to-field-path/toFieldPath';

const opts = {
    ADDITIONAL_EXPENSE_EXTRA_EXPENSE: { value: 'ADDITIONAL_EXPENSE_EXTRA_EXPENSE', label: 'Additional Expense/Extra Expense' },
    AGGREGATE_LIMIT: { value: 'AGGREGATE_LIMIT', label: 'Aggregate Limit' },
    BODILY_INJURY_PER_ACCIDENT: { value: 'BODILY_INJURY_PER_ACCIDENT', label: 'Bodily Injury Per Accident' },
    BODILY_INJURY_PER_PERSON: { value: 'BODILY_INJURY_PER_PERSON', label: 'Bodily Injury Per Person' },
    BUILDING_DWELLING: { value: 'BUILDING_DWELLING', label: 'Building/Dwelling' },
    BUSINESS_INCOME_LOSS_OF_INCOME: { value: 'BUSINESS_INCOME_LOSS_OF_INCOME', label: 'Business Income/Loss of Income' },
    COLLISION: { value: 'COLLISION', label: 'Collision' },
    COMBINED_SINGLE_LIMIT: { value: 'COMBINED_SINGLE_LIMIT', label: 'Combined Single Limit' },
    COMPLETED_VALUE: { value: 'COMPLETED_VALUE', label: 'Completed Value' },
    COMPREHENSIVE: { value: 'COMPREHENSIVE', label: 'Comprehensive' },
    COST_PER_ACRE: { value: 'COST_PER_ACRE', label: 'Cost Per Acre' },
    EARTHQUAKE: { value: 'EARTHQUAKE', label: 'Earthquake' },
    FLOOD: { value: 'FLOOD', label: 'Flood' },
    HAIL: { value: 'HAIL', label: 'Hail' },
    IMPROVEMENTS_AND_BETTERMENTS: { value: 'IMPROVEMENTS_AND_BETTERMENTS', label: 'Improvements and Betterments' },
    INCREASED_COST_OF_COMPLIANCE: { value: 'INCREASED_COST_OF_COMPLIANCE', label: 'Increased Cost of Compliance' },
    LOSS_OF_USE: { value: 'LOSS_OF_USE', label: 'Loss of Use' },
    NAMED_STORM: { value: 'NAMED_STORM', label: 'Named Storm' },
    OFF_PREMISES_STORAGE: { value: 'OFF_PREMISES_STORAGE', label: 'Off Premises Storage' },
    OTHER_FARM_STRUCTURES: { value: 'OTHER_FARM_STRUCTURES', label: 'Other Farm Structures' },
    OTHER_STRUCTURES: { value: 'OTHER_STRUCTURES', label: 'Other Structures' },
    OTHER_THAN_COMPREHENSIVE: { value: 'OTHER_THAN_COMPREHENSIVE', label: 'Other Than Comprehensive' },
    PER_OCCURRENCE_LIMIT: { value: 'PER_OCCURRENCE_LIMIT', label: 'Per Occurrence Limit' },
    PERSONAL_PROPERTY_CONTENTS: { value: 'PERSONAL_PROPERTY_CONTENTS', label: 'Personal Property/Contents' },
    PHYSICAL_DAMAGE: { value: 'PHYSICAL_DAMAGE', label: 'Physical Damage' },
    POLLUTION_LIABILITY: { value: 'POLLUTION_LIABILITY', label: 'Pollution Liability' },
    PROPERTY_DAMAGE_PER_ACCIDENT: { value: 'PROPERTY_DAMAGE_PER_ACCIDENT', label: 'Property Damage Per Accident' },
    SCHEDULED_AUTO_EQUIPMENT: { value: 'SCHEDULED_AUTO_EQUIPMENT', label: 'Scheduled Auto/Equipment' },
    SCHEDULED_FARM_PERSONAL_PROPERTY: { value: 'SCHEDULED_FARM_PERSONAL_PROPERTY', label: 'Scheduled Farm Personal Property' },
    SCHEDULED_LIMIT: { value: 'SCHEDULED_LIMIT', label: 'Scheduled Limit' },
    SCHEDULED_PROPERTY: { value: 'SCHEDULED_PROPERTY', label: 'Scheduled Property' },
    TRAILER_COLLISION: { value: 'TRAILER_COLLISION', label: 'Trailer Collision' },
    TRAILER_COMPREHENSIVE: { value: 'TRAILER_COMPREHENSIVE', label: 'Trailer Comprehensive' },
    TRAILER_INTERCHANGE: { value: 'TRAILER_INTERCHANGE', label: 'Trailer Interchange' },
    UNSCHEDULED_FARM_PERSONAL_PROPERTY: { value: 'UNSCHEDULED_FARM_PERSONAL_PROPERTY', label: 'Unscheduled Farm Personal Property' },
};

const typesMap: Record<TprmRequirementType, { value: string, label: string }[]> = {
    AUTOMOBILE_LIABILITY: [
        opts.AGGREGATE_LIMIT,
        opts.BODILY_INJURY_PER_ACCIDENT,
        opts.BODILY_INJURY_PER_PERSON,
        opts.COLLISION,
        opts.COMBINED_SINGLE_LIMIT,
        opts.COMPREHENSIVE,
        opts.PHYSICAL_DAMAGE,
        opts.POLLUTION_LIABILITY,
        opts.PROPERTY_DAMAGE_PER_ACCIDENT,
        opts.SCHEDULED_LIMIT,
        opts.TRAILER_COLLISION,
        opts.TRAILER_COMPREHENSIVE,
        opts.TRAILER_INTERCHANGE,
    ],
    BOATOWNERS: [
        opts.BODILY_INJURY_PER_ACCIDENT,
        opts.BODILY_INJURY_PER_PERSON,
        opts.COLLISION,
        opts.COMBINED_SINGLE_LIMIT,
        opts.COMPREHENSIVE,
        opts.PHYSICAL_DAMAGE,
        opts.PROPERTY_DAMAGE_PER_ACCIDENT,
        opts.SCHEDULED_LIMIT,
    ],
    BUILDERS_RISK: [
        opts.COMPLETED_VALUE,
        opts.EARTHQUAKE,
        opts.FLOOD,
        opts.OFF_PREMISES_STORAGE,
        opts.SCHEDULED_LIMIT,
    ],
    COMMERCIAL_AUTO_PHYSICAL_DAMAGE: [
        opts.COLLISION,
        opts.COMPREHENSIVE,
        opts.OTHER_THAN_COMPREHENSIVE,
        opts.SCHEDULED_AUTO_EQUIPMENT,
        opts.SCHEDULED_LIMIT,
        opts.TRAILER_INTERCHANGE,
    ],
    CROP_INSURANCE: [
        opts.COST_PER_ACRE,
        opts.HAIL,
        opts.SCHEDULED_LIMIT,
        opts.SCHEDULED_PROPERTY,
    ],
    DWELLING: [
        opts.ADDITIONAL_EXPENSE_EXTRA_EXPENSE,
        opts.BUILDING_DWELLING,
        opts.BUSINESS_INCOME_LOSS_OF_INCOME,
        opts.EARTHQUAKE,
        opts.FLOOD,
        opts.HAIL,
        opts.IMPROVEMENTS_AND_BETTERMENTS,
        opts.INCREASED_COST_OF_COMPLIANCE,
        opts.LOSS_OF_USE,
        opts.OTHER_STRUCTURES,
        opts.PERSONAL_PROPERTY_CONTENTS,
        opts.SCHEDULED_LIMIT,
        opts.SCHEDULED_PROPERTY,
    ],
    FARMOWNERS: [
        opts.ADDITIONAL_EXPENSE_EXTRA_EXPENSE,
        opts.BUILDING_DWELLING,
        opts.BUSINESS_INCOME_LOSS_OF_INCOME,
        opts.EARTHQUAKE,
        opts.FLOOD,
        opts.HAIL,
        opts.IMPROVEMENTS_AND_BETTERMENTS,
        opts.INCREASED_COST_OF_COMPLIANCE,
        opts.LOSS_OF_USE,
        opts.OTHER_FARM_STRUCTURES,
        opts.OTHER_STRUCTURES,
        opts.PERSONAL_PROPERTY_CONTENTS,
        opts.SCHEDULED_LIMIT,
        opts.SCHEDULED_FARM_PERSONAL_PROPERTY,
        opts.SCHEDULED_PROPERTY,
        opts.UNSCHEDULED_FARM_PERSONAL_PROPERTY,
    ],
    FLEET: [
        opts.AGGREGATE_LIMIT,
        opts.BODILY_INJURY_PER_ACCIDENT,
        opts.BODILY_INJURY_PER_PERSON,
        opts.COLLISION,
        opts.COMBINED_SINGLE_LIMIT,
        opts.COMPREHENSIVE,
        opts.PHYSICAL_DAMAGE,
        opts.POLLUTION_LIABILITY,
        opts.PROPERTY_DAMAGE_PER_ACCIDENT,
        opts.SCHEDULED_LIMIT,
    ],
    FLOOD_INSURANCE: [
        opts.ADDITIONAL_EXPENSE_EXTRA_EXPENSE,
        opts.BUILDING_DWELLING,
        opts.BUSINESS_INCOME_LOSS_OF_INCOME,
        opts.IMPROVEMENTS_AND_BETTERMENTS,
        opts.INCREASED_COST_OF_COMPLIANCE,
        opts.LOSS_OF_USE,
        opts.OTHER_FARM_STRUCTURES,
        opts.OTHER_STRUCTURES,
        opts.PERSONAL_PROPERTY_CONTENTS,
        opts.SCHEDULED_LIMIT,
        opts.SCHEDULED_FARM_PERSONAL_PROPERTY,
        opts.SCHEDULED_PROPERTY,
        opts.UNSCHEDULED_FARM_PERSONAL_PROPERTY,
    ],
    HOMEOWNERS: [
        opts.ADDITIONAL_EXPENSE_EXTRA_EXPENSE,
        opts.BUILDING_DWELLING,
        opts.BUSINESS_INCOME_LOSS_OF_INCOME,
        opts.EARTHQUAKE,
        opts.FLOOD,
        opts.HAIL,
        opts.IMPROVEMENTS_AND_BETTERMENTS,
        opts.INCREASED_COST_OF_COMPLIANCE,
        opts.LOSS_OF_USE,
        opts.OTHER_STRUCTURES,
        opts.PERSONAL_PROPERTY_CONTENTS,
        opts.SCHEDULED_LIMIT,
        opts.SCHEDULED_PROPERTY,
    ],
    HULL_AND_MACHINERY: [
        opts.NAMED_STORM,
        opts.PERSONAL_PROPERTY_CONTENTS,
        opts.PHYSICAL_DAMAGE,
        opts.POLLUTION_LIABILITY,
        opts.PROPERTY_DAMAGE_PER_ACCIDENT,
        opts.SCHEDULED_LIMIT,
    ],
    INLAND_MARINE: [
        opts.ADDITIONAL_EXPENSE_EXTRA_EXPENSE,
        opts.BUILDING_DWELLING,
        opts.BUSINESS_INCOME_LOSS_OF_INCOME,
        opts.EARTHQUAKE,
        opts.FLOOD,
        opts.HAIL,
        opts.IMPROVEMENTS_AND_BETTERMENTS,
        opts.INCREASED_COST_OF_COMPLIANCE,
        opts.LOSS_OF_USE,
        opts.OTHER_FARM_STRUCTURES,
        opts.OTHER_STRUCTURES,
        opts.PERSONAL_PROPERTY_CONTENTS,
        opts.SCHEDULED_LIMIT,
        opts.SCHEDULED_FARM_PERSONAL_PROPERTY,
        opts.SCHEDULED_PROPERTY,
        opts.UNSCHEDULED_FARM_PERSONAL_PROPERTY,
    ],
    LOSS_OF_INCOME: [
        opts.ADDITIONAL_EXPENSE_EXTRA_EXPENSE,
        opts.BUSINESS_INCOME_LOSS_OF_INCOME,
        opts.SCHEDULED_LIMIT,
        opts.SCHEDULED_PROPERTY,
    ],
    NON_TRUCKING_LIABILITY: [
        opts.AGGREGATE_LIMIT,
        opts.BODILY_INJURY_PER_ACCIDENT,
        opts.BODILY_INJURY_PER_PERSON,
        opts.COLLISION,
        opts.COMBINED_SINGLE_LIMIT,
        opts.COMPREHENSIVE,
        opts.PHYSICAL_DAMAGE,
        opts.POLLUTION_LIABILITY,
        opts.PROPERTY_DAMAGE_PER_ACCIDENT,
        opts.SCHEDULED_LIMIT,
        opts.TRAILER_COLLISION,
        opts.TRAILER_COMPREHENSIVE,
        opts.TRAILER_INTERCHANGE,
    ],
    PROPERTY_INSURANCE: [
        opts.ADDITIONAL_EXPENSE_EXTRA_EXPENSE,
        opts.BUILDING_DWELLING,
        opts.BUSINESS_INCOME_LOSS_OF_INCOME,
        opts.EARTHQUAKE,
        opts.FLOOD,
        opts.HAIL,
        opts.IMPROVEMENTS_AND_BETTERMENTS,
        opts.INCREASED_COST_OF_COMPLIANCE,
        opts.LOSS_OF_USE,
        opts.OTHER_FARM_STRUCTURES,
        opts.OTHER_STRUCTURES,
        opts.PERSONAL_PROPERTY_CONTENTS,
        opts.SCHEDULED_LIMIT,
        opts.SCHEDULED_FARM_PERSONAL_PROPERTY,
        opts.SCHEDULED_PROPERTY,
        opts.UNSCHEDULED_FARM_PERSONAL_PROPERTY,
    ],
    UMBRELLA_LIABILITY: [
        opts.AGGREGATE_LIMIT,
        opts.PER_OCCURRENCE_LIMIT,
        opts.SCHEDULED_LIMIT,
    ],
    WATERCRAFT: [
        opts.BODILY_INJURY_PER_ACCIDENT,
        opts.BODILY_INJURY_PER_PERSON,
        opts.COLLISION,
        opts.COMBINED_SINGLE_LIMIT,
        opts.COMPREHENSIVE,
        opts.PHYSICAL_DAMAGE,
        opts.PROPERTY_DAMAGE_PER_ACCIDENT,
        opts.SCHEDULED_LIMIT,
    ],
    YACHT_INSURANCE: [
        opts.BODILY_INJURY_PER_ACCIDENT,
        opts.BODILY_INJURY_PER_PERSON,
        opts.COLLISION,
        opts.COMBINED_SINGLE_LIMIT,
        opts.COMPREHENSIVE,
        opts.PHYSICAL_DAMAGE,
        opts.PROPERTY_DAMAGE_PER_ACCIDENT,
        opts.SCHEDULED_LIMIT,
    ],
} as Record<TprmRequirementType, { value: string, label: string }[]>;

const alias: Record<TprmRequirementType, { value: string, label: string }[]> = {
    BUSINESS_AUTO_LIABILITY: typesMap['AUTOMOBILE_LIABILITY' as TprmRequirementType],
    UMBRELLA_EXCESS_LIABILITY: typesMap['UMBRELLA_LIABILITY' as TprmRequirementType],
} as Record<TprmRequirementType, { value: string, label: string }[]>;

const defaultOptions = Object.values(opts).sort((a, b) =>
    a.label.localeCompare(b.label),
);

export function getLimitTypeOptionsForRequirementType(
    requirementType: TprmRequirementType,
): { value: string, label: string }[] {
    const limitTypes = typesMap[requirementType] || alias[requirementType];
    // Return default set if requirement type not found
    return limitTypes || defaultOptions;
}

export function createCollateralFieldMeta(schema: FieldSchema, requirementType?: TprmRequirementType): FieldMeta {
    const limitTypeOptions = requirementType
        ? getLimitTypeOptionsForRequirementType(requirementType)
        : [];

    const limitTypeItemMeta = {
        label: 'Limit Type',
        type: { $type: 'Object' },
        required: true,
        properties: {
            limitType: {
                label: 'Limit Type',
                type: {
                    $type: 'Enum',
                    values: limitTypeOptions,
                },
            },
            coverageLimit: { label: 'Coverage Limit', type: { $type: 'Int' } },
            valuation: {
                label: 'Valuation',
                type: {
                    $type: 'Enum',
                    values: [
                        { value: 'ACV', label: 'ACV' },
                        { value: 'RCV', label: 'RCV' },
                    ],
                },
            },
            deductible: { label: 'Deductible', type: { $type: 'Int' } },
        },
        defaultValue: {
            valuation: undefined,
            coverageLimit: undefined,
            deductible: undefined,
            limitType: undefined,
        },
    };
    const properties: Record<string, FieldMeta> = {
        uniqueIdentifier: {
            path: toFieldPath('uniqueIdentifier'),
            type: { $type: 'String' },
            label: 'Unique Identifier',
            required: true,
            defaultValue: undefined,
        },
        description: {
            path: toFieldPath('description'),
            type: { $type: 'String' },
            label: 'Description',
            required: true,
            defaultValue: undefined,
        },
        limitTypes: {
            label: 'Limit Types',
            path: toFieldPath('limitTypes'),
            type: {
                $type: 'List',
                elementType: { $type: 'Object' },
            },
            items: limitTypeItemMeta as unknown as FieldMeta,
            required: true,
            defaultValue: [ limitTypeItemMeta.defaultValue ],
        },
    };
    const defaultValue = {
        uniqueIdentifier: undefined,
        description: undefined,
        limitTypes: [ limitTypeItemMeta.defaultValue ],
    };
    return { ...schema, properties, defaultValue };
}
